import { useIntl } from 'react-intl';
import { format } from 'date-fns';
import { enUS, es, ko, ru, vi, zhCN } from 'date-fns/locale';
import {
  parseMilliseconds,
  utcToTriMetTimeZone,
} from '#/shared/utils/date-time';
import {
  TRIMET_DATE_FORMAT,
  TRIMET_TIME_FORMAT,
  TRIMET_LONG_DATE_FORMAT,
  ENGLISH_LOCALE_CODE,
  CHINESE_LOCALE_CODE,
} from '#/shared/constants';

// `date-fns` uses different locale codes than `react-intl` in some cases
// so this lookup resolves those discrepancies
const dateFnsLocaleLookup = {
  [ENGLISH_LOCALE_CODE]: enUS,
  es,
  ko,
  ru,
  vi,
  [CHINESE_LOCALE_CODE]: zhCN,
};

/**
 * A collection of functions and objects that aid in the conversion of datetime
 * objects and values to strings in the user's selected language
 */
const useIntlDatetime = () => {
  const intl = useIntl();
  const dateFnsLocale = dateFnsLocaleLookup[intl.locale] || enUS;

  /**
   * Format a Date object into TriMet date string format and time zone
   *
   * @param {Date} date
   * @param {Boolean} longFormat If `true` the date will be returned using the
   * app's long form date format
   * @returns {String}
   */
  const formatToTriMetDateString = (date, longFormat = false) =>
    format(
      utcToTriMetTimeZone(date),
      longFormat ? TRIMET_LONG_DATE_FORMAT : TRIMET_DATE_FORMAT,
      { locale: dateFnsLocale }
    );

  /**
   * Format a Date object into TriMet time string format and time zone
   *
   * @param {Date} date
   * @returns {string}
   */
  const formatToTriMetTimeString = date =>
    format(utcToTriMetTimeZone(date), TRIMET_TIME_FORMAT, {
      locale: dateFnsLocale,
    });

  return {
    dateFnsLocale,
    formatToTriMetDateString,
    formatToTriMetTimeString,

    /**
     * Convert milliseconds timestamp to TriMet date string format
     *
     * @param milliSecondsTimeStamp
     * @param {Boolean} longFormat If `true` the date will be returned using the
     * app's long form date format
     * @returns {String}
     */
    milliSecondsToTriMetDateString: (
      milliSecondsTimeStamp,
      longFormat = false
    ) =>
      formatToTriMetDateString(
        parseMilliseconds(milliSecondsTimeStamp),
        longFormat
      ),

    /**
     * Convert milliseconds timestamp to TriMet time string format
     *
     * @param {Number|String} milliSecondsTimeStamp Milliseconds since the epoch
     * @returns {String}
     */
    milliSecondsToTriMetTimeString: milliSecondsTimeStamp =>
      formatToTriMetTimeString(parseMilliseconds(milliSecondsTimeStamp)),
  };
};

export default useIntlDatetime;
